<script setup>
import * as Sentry from "@sentry/vue";
import { onMounted, ref, watchEffect, provide } from "vue";
import VideoAuth from "./components/VideoAuth.vue";
import UnsupportedBrowser from "./components/UnsupportedBrowser.vue";
import isSupportedBrowser from "./utils/browser.js";
import { useI18n } from "vue-i18n";
import getDeviceInfo from "./utils/deviceInfo.js";
import { getBrandingConfiguration, getBrandingConfigurationByKey } from "./utils/getBrandingConfiguration.js";
import { runningAsWechatMP } from "./utils/globalReferences.js";

const { locale } = useI18n();
const queryScanId = ref(null);
const isBrowserSupported = ref(false);
const unsupportedBrowserReason = ref("");
const deviceInfo = ref({});
const loadingDeviceInfo = ref(true);
const hasWasmCrashed = ref(false);

const brandingTheme = ref({});
provide("brandingTheme", brandingTheme);

const hideHeader = ref(false);
provide("noHeader", hideHeader);

const fetchDeviceInfo = async () => {
  try {
    deviceInfo.value = await getDeviceInfo();
    const { supported, reason } = isSupportedBrowser(deviceInfo.value);
    isBrowserSupported.value = supported;
    unsupportedBrowserReason.value = reason;
  } catch (error) {
    console.error("Failed to get device info:", error);
    Sentry.captureException(new Error("Failed to get device info", { cause: error }));
  }
  loadingDeviceInfo.value = false;
};

const updateRtlAttribute = () => {
  const isRtl = ["fa"].includes(locale.value);
  document.documentElement.setAttribute("lang", locale.value);
  document.documentElement.setAttribute("dir", isRtl ? "rtl" : "ltr");
};

onMounted(async () => {
  const url = new URL(window.location.href);
  queryScanId.value = url.searchParams.get("uid");
  const apiKey = url.searchParams.get("api_key");
  const lpKey = url.searchParams.get("lp_key");

  if (url.searchParams.has("no-header")) {
    hideHeader.value = true;
  }

  try {
    if (lpKey) {
      brandingTheme.value = await getBrandingConfigurationByKey(lpKey);
    } else if (queryScanId.value && apiKey) {
      brandingTheme.value = await getBrandingConfiguration(queryScanId.value, apiKey);
    }

    const themeKeys = Object.keys(brandingTheme.value || {});
    if (themeKeys.length !== 0) {
      document.title = "Authentication";
    }

    if (brandingTheme.value?.forceDefaultLanguage) {
      locale.value = brandingTheme.value.forceDefaultLanguage;
    }
  } catch (error) {
    Sentry.captureException(new Error(`Error setting branding theme: ${error.message}`));
    brandingTheme.value = {};
  }

  await fetchDeviceInfo();
  detectWASMCrash();
  updateRtlAttribute();
  runningAsWechatMP.value = !!deviceInfo.value?.isWechatMiniProgram?.();
});

watchEffect(() => {
  updateRtlAttribute();
});

const detectWASMCrash = () => {
  try {
    if (window.localStorage.getItem("__scanner_wasm_init__") === "loaded") {
      hasWasmCrashed.value = true;
      Sentry.captureMessage("WASM runtime did not initialize on the previous visit", "warning");
      window.localStorage.removeItem("__scanner_wasm_init__");
      window.location.href = `${window.location.origin}/${import.meta.env.VITE_PHOTO_AUTH_PREFIX}${window.location.search}`;
    }
  } catch (err) {}
};
</script>

<template>
<Suspense>
  <div v-if="loadingDeviceInfo" class="text_all preload">
    {{ $t("loading") }}
  </div>
  <div v-else class="text_all">
    <VideoAuth v-if="isBrowserSupported" :query-scan-id="queryScanId" :device-info="deviceInfo" :has-wasm-crashed="hasWasmCrashed" />
    <UnsupportedBrowser v-else :device-info="deviceInfo" :unsupported-browser-reason="unsupportedBrowserReason" />
  </div>
</Suspense>
</template>

<style>
.preload {
  font-size: 20px;
  font-weight: 500;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #ffffff;
}
</style>
